import { Skeleton } from "antd"
import React from "react"
import { useNavigate } from "react-router-dom"

import { LEAD_KPI_ICONS, type LeadKpiIconKey } from "./lead-kpi-icons.tsx"

export interface LeadKpiTileData {
  id: string
  title: string
  value: number | string
  icon: LeadKpiIconKey
  /** The glyph's own colour — the design gives each metric a different one. */
  tint: string
  delta?: {
    direction: "up" | "down" | "flat"
    /** Whether the move is good news for this metric, which decides the colour. */
    tone: "good" | "bad" | "flat"
    text: string
    label: string
  } | null
  drillTo?: string
}

const DELTA_COLOURS = {
  good: "#16A34A",
  bad: "#EF4444",
  // No movement is not good news or bad; colouring it either way would read as
  // a judgement the number is not making.
  flat: "#94A3B8"
} as const

const DELTA_ARROWS = {
  up: "↑",
  down: "↓",
  flat: "—"
} as const

/**
 * The design's KPI tile, which differs from the shared `KpiCard` in every
 * detail that reads at a glance: the label is title case rather than uppercase,
 * the glyph is a bare coloured icon rather than a tinted square, and the delta
 * sits on its own line under the value. Reusing `KpiCard` here would either
 * miss the design or drag every dashboard tile in the app along with it.
 */
export function LeadKpiTile({
  tile,
  loading,
  selected
}: {
  tile: LeadKpiTileData
  loading?: boolean
  selected?: boolean
}) {
  const navigate = useNavigate()
  // A payload naming a glyph this build does not have must not take the page
  // down with it — the backend can add tiles ahead of the client.
  const Icon = LEAD_KPI_ICONS[tile.icon] ?? LEAD_KPI_ICONS.people
  const clickable = Boolean(tile.drillTo)
  const drill = () => navigate(tile.drillTo as string)

  return (
    <div
      data-testid="lead-kpi-tile"
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      // Selection is the tile's whole answer to "why is the table this short",
      // so it has to reach a screen reader as a pressed state, not just a border.
      aria-pressed={clickable ? Boolean(selected) : undefined}
      onClick={clickable ? drill : undefined}
      onKeyDown={
        clickable
          ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") return
              event.preventDefault()
              drill()
            }
          : undefined
      }
      className={[
        "rounded-xl border bg-white px-4 py-3",
        selected
          ? "border-blue-500 ring-1 ring-blue-500"
          : "border-slate-200",
        clickable && !selected ? "hover:border-slate-300" : "",
        clickable ? "cursor-pointer" : ""
      ].join(" ")}
    >
      <div className={"flex items-center gap-2"}>
        <span style={{ color: tile.tint, lineHeight: 0 }}>
          <Icon />
        </span>
        <span className={"truncate text-[13px] font-medium text-slate-600"}>
          {tile.title}
        </span>
      </div>

      {loading ? (
        <Skeleton.Node active style={{ width: 72, height: 34, marginTop: 6 }} />
      ) : (
        <div
          data-testid="lead-kpi-value"
          className={
            "mt-1 text-[30px] font-bold leading-tight tracking-tight text-slate-900"
          }
        >
          {tile.value}
        </div>
      )}

      {/* The slot is reserved even when empty so a tile carrying a delta and one
          without it stay the same height and the row does not step. */}
      <div className={"mt-0.5 flex min-h-[16px] items-center gap-1 text-[11px]"}>
        {tile.delta && (
          <>
            <span
              style={{ color: DELTA_COLOURS[tile.delta.tone] }}
              className={"shrink-0 font-semibold"}
            >
              {DELTA_ARROWS[tile.delta.direction]} {tile.delta.text}
            </span>
            {/* The server writes the label and can qualify it — a narrower
                caller is told the trend is the company's — so it has to survive
                being longer than the tile is wide. */}
            <span className={"truncate text-slate-400"} title={tile.delta.label}>
              {tile.delta.label}
            </span>
          </>
        )}
      </div>
    </div>
  )
}
